import { MODULES_BY_KEY, type ModuleDashboard as LiveData, type ModuleKey } from '@ruby/shared';
import { useQuery } from '@tanstack/react-query';
import { Link, useParams } from '@tanstack/react-router';
import { BarChart3, Plus } from 'lucide-react';
import { lazy, Suspense } from 'react';
import { screenIcon } from '@/app/screen-icons';
import { ModuleHero, HBars, Kpi, MonthBars, Panel } from '@/components/dashboard/dash-parts';
import { EmptyState, Skeleton, Spinner } from '@/components/ui/misc';
import { api } from '@/lib/api';
import { usePermissions, visibleMenus } from '@/lib/permissions';
import { useSession } from '@/lib/session';
import { cn } from '@/lib/utils';

const AdminDashboard = lazy(() => import('../admin/admin-dashboard'));
const AccountingDashboard = lazy(() => import('../accounting/dashboard'));
const InventoryDashboard = lazy(() => import('../inventory/dashboard'));
const HrDashboard = lazy(() => import('../hr/dashboard'));
const MattressDashboard = lazy(() => import('../mattress/dashboard'));

export function greeting() {
  const h = Number(new Intl.DateTimeFormat('en-GB', { hour: 'numeric', hour12: false, timeZone: 'Asia/Dubai' }).format(new Date()));
  return h < 12 ? 'Good morning' : h < 17 ? 'Good afternoon' : 'Good evening';
}

/** Green welcome card used at the top of every module dashboard. */
export function DashboardHero({ title, subtitle, figureLabel, figure, note, badge }: { title: string; subtitle: string; figureLabel: string; figure: React.ReactNode; note?: string; badge?: string }) {
  return (
    <section className="relative overflow-hidden rounded-[20px] bg-[linear-gradient(135deg,#0f5132_0%,#14704a_60%,#1f8a5b_100%)] p-6 text-white">
      <svg viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,.1)" strokeWidth="1" aria-hidden className="pointer-events-none absolute -right-8 -bottom-10 size-52">
        <path d="M3 21V9l6 4V9l6 4V5l6 4v12z" />
      </svg>
      <div className="relative flex items-center justify-between gap-3">
        <h1 className="text-[19px] font-bold">{title}</h1>
        {badge && <span className="rounded-full bg-white/20 px-2.5 py-1 text-[11px] font-bold">{badge}</span>}
      </div>
      <p className="relative mt-1 text-[13px] text-emerald-100">{subtitle}</p>
      <p className="relative mt-5 text-[13px] text-emerald-100">{figureLabel}</p>
      <p className="tabular relative text-[40px] leading-tight font-extrabold tracking-tight">{figure}</p>
      {note && <p className="relative text-[13px] font-semibold text-emerald-200">{note}</p>}
    </section>
  );
}

/** Each module has its own dashboard. Business modules show KPI slots until their data exists. */
export function ModuleDashboard() {
  const { module } = useParams({ strict: false }) as { module: ModuleKey };
  const boot = useSession((s) => s.boot)!;
  const { can } = usePermissions();
  const def = MODULES_BY_KEY[module];
  if (module === 'admin') {
    return (
      <Suspense fallback={<Spinner />}>
        <AdminDashboard />
      </Suspense>
    );
  }
  if (module === 'mattress') {
    return (
      <Suspense fallback={<Spinner />}>
        <MattressDashboard />
      </Suspense>
    );
  }
  if (module === 'hr') {
    return (
      <Suspense fallback={<Spinner />}>
        <HrDashboard />
      </Suspense>
    );
  }
  if (module === 'inventory') {
    return (
      <Suspense fallback={<Spinner />}>
        <InventoryDashboard />
      </Suspense>
    );
  }
  if (module === 'accounting') {
    return (
      <Suspense fallback={<Spinner />}>
        <AccountingDashboard />
      </Suspense>
    );
  }
  const menus = visibleMenus(boot.permissions, module);
  const screens = menus.flatMap((m) => m.screens);
  // Quick create: everyday documents and records, not set-up screens.
  const creatable = menus.filter((m) => !['settings', 'setup'].includes(m.key)).flatMap((m) => m.screens).filter((s) => !s.hidden && can(module, s.key, 'create')).slice(0, 5);
  return <LiveModuleDashboard module={module} creatable={creatable} screens={screens} def={def} />;

}

const fmt = (v: number | string, f?: 'money' | 'count' | 'text'): string => (typeof v === 'string' ? v : f === 'count' ? v.toLocaleString('en-US') : v.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }));

/** Live dashboard of a business module: quick create, figures (each opening its screen), 12-month trend and ranked lists. */
function LiveModuleDashboard({ module, creatable, def }: { module: ModuleKey; creatable: { key: string; label: string }[]; screens: { key: string; label: string }[]; def: (typeof MODULES_BY_KEY)[ModuleKey] }) {
  const { data } = useQuery({ queryKey: ['insights', 'dashboard', module], queryFn: () => api<LiveData>(`/insights/dashboard/${module}`), refetchInterval: 120_000 });
  const tones = ['neutral', 'warning', 'danger', 'success'] as const;
  const [first, ...more] = data?.breakdowns ?? [];
  return (
    <>
      <ModuleHero module={module} title={def.name} subtitle={<>{def.description}, live.</>}>
        {creatable.map((s) => {
          const I = screenIcon(s.key);
          return (
            <Link key={s.key} to="/m/$module/$screen/$id" params={{ module, screen: s.key, id: 'new' }} className="inline-flex items-center gap-1.5 rounded-xl bg-white/15 px-3 py-2 text-[13px] font-semibold text-white ring-1 ring-white/20 backdrop-blur transition-all hover:-translate-y-0.5 hover:bg-white hover:text-emerald-900">
              <Plus className="size-3.5" /><I className="size-4" /> {s.label}
            </Link>
          );
        })}
      </ModuleHero>
      {!data ? (
        <div className="grid gap-4"><Skeleton className="h-28" /><Skeleton className="h-72" /></div>
      ) : (
        <div className="flex flex-col gap-4">
          <div className={cn('grid gap-4 sm:grid-cols-2', (data.kpis.length + (data.hero ? 1 : 0)) >= 5 ? 'xl:grid-cols-5' : 'xl:grid-cols-4')}>
            {data.hero && <Kpi module={module} label={data.hero.label} value={fmt(data.hero.value, data.hero.format)} hint={data.hero.note} tone="primary" />}
            {data.kpis.map((k, i) => (
              <Kpi key={k.label} module={module} label={k.label} value={fmt(k.value, k.format)} hint={k.hint} tone={k.tone ?? tones[i % 1]!} negative={k.tone === 'danger' && typeof k.value === 'number' && k.value > 0} to={k.screen ? { screen: k.screen } : undefined} />
            ))}
          </div>
          <div className="grid gap-4 xl:grid-cols-[minmax(0,3fr)_minmax(0,2fr)]">
            <Panel module={module} title={data.trend?.title ?? 'Trend'} sub="Last 12 months">
              {data.trend && data.trend.points.some((p) => p.value) ? <MonthBars rows={data.trend.points.map((p) => ({ month: p.label, value: p.value }))} label={data.trend.title.split(' (')[0]!} /> : <EmptyState icon={<BarChart3 />} title="No data yet" />}
            </Panel>
            {first && (
              <Panel module={module} title={first.title} sub={first.sub} link={first.screen ? { screen: first.screen } : undefined}>
                <HBars module={module} rows={first.rows.map((r) => ({ key: r.label, label: r.label, value: r.value }))} decimals={first.rows[0]?.format === "count" ? 0 : 2} empty="Nothing yet." />
              </Panel>
            )}
          </div>
          {more.length > 0 && (
            <div className="grid gap-4 xl:grid-cols-2">
              {more.map((b) => (
                <Panel key={b.title} module={module} title={b.title} sub={b.sub} link={b.screen ? { screen: b.screen } : undefined}>
                  <HBars module={module} rows={b.rows.map((r) => ({ key: r.label, label: r.label, value: r.value }))} decimals={b.rows[0]?.format === "count" ? 0 : 2} color="bg-[#4c6fd1]/75" empty="Nothing yet." />
                </Panel>
              ))}
            </div>
          )}
        </div>
      )}
    </>
  );
}
